import type { components } from "@majitap/sdk";
import { queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";
import type { Tone } from "./labels";

type Schemas = components["schemas"];
export type StaffMember = Schemas["StaffMember"];
export type Role = Schemas["Role"];
export type Permission = Schemas["PermissionList"]["data"][number];
export type Activity = Schemas["Activity"];

/** What a staff member's badge says: Invited until they set a password, else their status. */
export function staffBadge(s: Pick<StaffMember, "status" | "invitation_pending">): [string, Tone] {
  if (s.status !== "active") return [s.status === "suspended" ? "Suspended" : "Deactivated", "neutral"];
  return s.invitation_pending ? ["Invited", "warning"] : ["Active", "success"];
}

/**
 * What a staff member's page offers. Resending is for someone active (deactivated is
 * STAFF_INACTIVE); a password reset, for someone active who has a password (INVITATION_PENDING).
 */
export const staffActionsFor = (s: Pick<StaffMember, "status" | "invitation_pending">) => ({
  resend: s.status === "active",
  resetPassword: s.status === "active" && !s.invitation_pending,
  deactivate: s.status === "active",
  reactivate: s.status !== "active",
});

/** The areas permissions belong to, by their prefix ("orders.write" → Orders), in menu order. */
const AREAS: Record<string, string> = {
  orders: "Orders",
  deliveries: "Dispatch",
  customers: "Customers",
  fleet: "Fleet",
  partners: "Partners",
  invoices: "Invoices",
  payments: "Payments",
  wallets: "Wallets",
  payouts: "Payouts",
  ledger: "Accounting",
  catalog: "Products",
  pricing: "Pricing",
  staff: "Team",
  integrations: "Connections",
  readiness: "Cut-over",
};

/** Permissions grouped by area, each with its plain meaning (the catalogue's description). */
export function permissionGroups(permissions: Permission[]): { area: string; permissions: Permission[] }[] {
  const groups = new Map<string, Permission[]>();
  for (const p of permissions) {
    const prefix = p.id.split(".")[0];
    const area = AREAS[prefix] ?? prefix.charAt(0).toUpperCase() + prefix.slice(1);
    groups.set(area, [...(groups.get(area) ?? []), p]);
  }
  const order = Object.values(AREAS);
  return [...groups.entries()]
    .sort(([a], [b]) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99))
    .map(([area, list]) => ({ area, permissions: list.sort((a, b) => a.id.localeCompare(b.id)) }));
}

export const rolesQuery = queryOptions({
  queryKey: ["staff", "roles"],
  queryFn: async () => (await ok(api.GET("/v1/admin/staff/roles"))).data,
});

export const permissionsQuery = queryOptions({
  queryKey: ["staff", "permissions"],
  staleTime: 10 * 60_000,
  queryFn: async () => (await ok(api.GET("/v1/admin/staff/permissions"))).data,
});

export const staffQuery = (id: string) =>
  queryOptions({
    queryKey: ["staff", "member", id],
    queryFn: () => ok(api.GET("/v1/admin/staff/{id}", { params: { path: { id } } })),
  });

/** Every staff member, for the audit log's "who" filter (a company has tens). */
export const allStaffQuery = queryOptions({
  queryKey: ["staff", "all"],
  queryFn: async () => (await ok(api.GET("/v1/admin/staff", { params: { query: { limit: 100 } } }))).data,
});

const path = (id: string) => ({ params: { path: { id } } });

export const teamActions = {
  invite: (body: Schemas["InviteStaffBody"]) => ok(api.POST("/v1/admin/staff", { body })),
  setRoles: (id: string, roleIds: string[]) =>
    ok(api.PUT("/v1/admin/staff/{id}/roles", { ...path(id), body: { role_ids: roleIds } })),
  deactivate: (id: string) => ok(api.POST("/v1/admin/staff/{id}/deactivate", path(id))),
  reactivate: (id: string) => ok(api.POST("/v1/admin/staff/{id}/reactivate", path(id))),
  update: (id: string, body: Schemas["UpdateStaffBody"]) =>
    ok(api.PATCH("/v1/admin/staff/{id}", { ...path(id), body })),
  deleteRole: (id: string) => ok(api.DELETE("/v1/admin/staff/roles/{id}", path(id))),
  resend: (id: string) => ok(api.POST("/v1/admin/staff/{id}/invite", path(id))),
  resetPassword: (id: string) => ok(api.POST("/v1/admin/staff/{id}/password-reset", path(id))),
  createRole: (body: Schemas["CreateRoleBody"]) => ok(api.POST("/v1/admin/staff/roles", { body })),
  updateRole: (id: string, body: Schemas["UpdateRoleBody"]) =>
    ok(api.PATCH("/v1/admin/staff/roles/{id}", { ...path(id), body })),
};

/** Where an audit entry's record opens in the console, when it has a page. */
export function targetHref(type: string, id: string | null): string | null {
  if (!id) return null;
  const pages: Record<string, string> = {
    order: `/orders/${id}`,
    customer: `/customers/${id}`,
    business: `/businesses/${id}`,
    partner: `/partners/${id}`,
    invoice: `/invoices/${id}`,
    payment: `/payments/${id}`,
    wallet: `/wallets/${id}`,
    payout_request: `/payouts/${id}`,
    product: `/products/${id}`,
    pricing: `/pricing/${id}`,
    truck: `/fleet?truck=${id}`,
    staff: `/settings/staff/${id}`,
    integration: `/settings/integrations/${id}`,
  };
  return pages[type] ?? null;
}

/** Every staff member's name (P15-14): for who won a customer or manages a business. Any staff can read it. */
export const staffNamesQuery = queryOptions({
  queryKey: ["staff", "names"],
  staleTime: 5 * 60_000,
  queryFn: async () => (await ok(api.GET("/v1/admin/staff/names"))).data,
});
